import {
    setPersistence, browserSessionPersistence,
    signInWithPopup, GoogleAuthProvider, TwitterAuthProvider, signInWithPhoneNumber, RecaptchaVerifier,
    ConfirmationResult, GithubAuthProvider, OAuthProvider, AuthProvider, User,
    Auth, getAuth
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { ReportErrorToServer } from "../Lib/ReportErrorToServer";
import { PageBusyIndicator } from "../Lib/PageBusyIndicator";

export function SignInInit() {
    new SignInViaFirebase();
}

const MyFirebaseConfig = {
    // Google API key docs: https://cloud.google.com/docs/authentication/api-keys#securing_an_api_key
    // At Firebase: https://console.firebase.google.com/u/1/project/btc-web/settings/general
    // At Google console: https://console.cloud.google.com/auth/clients?authuser=1&inv=1&invt=Aby5PA&project=btc-web
    apiKey: "AIzaSyBLp9uv6COqH1Yx_bnmi_yj5TzvdG90914",
    authDomain: "btc-web.firebaseapp.com",
    databaseURL: "https://btc-web.firebaseio.com",
    projectId: "btc-web",
    storageBucket: "btc-web.appspot.com",
    messagingSenderId: "659862730493",
    appId: "1:659862730493:web:3699831cc6f30a12ca3094",
    measurementId: "G-87M9C81G1T",
}

// Added 11/18/2018 Google Firebase auth support from: https://console.firebase.google.com/u/0/project/btc-web/authentication/users
class SignInViaFirebase {
    private confirmationResultForPhone: ConfirmationResult | null = null;
    private recaptchaVerifierForPhone: RecaptchaVerifier | null = null;
    protected readonly auth: Auth;
    private readonly pageBusyIndicator = new PageBusyIndicator("idTSSpinner");

    constructor() {
        const app = initializeApp(MyFirebaseConfig);
        this.auth = getAuth(app);

        this.pageBusyIndicator.Increment(); // Increment for recaptcha setup processing

        // Sign-in by federated provider buttons
        const butGoogle = document.getElementById("butSignInWithGoogle") as HTMLButtonElement;
        butGoogle?.addEventListener("click", async () => { await this.SignInWithKnownProvider(new GoogleAuthProvider(), butGoogle) });

        const butGitHub = document.getElementById("butSignInWithGithub") as HTMLButtonElement;
        butGitHub?.addEventListener("click", async () => { await this.SignInWithKnownProvider(new GithubAuthProvider(), butGitHub) });

        const butYahoo = document.getElementById("butSignInWithYahoo") as HTMLButtonElement;
        butYahoo?.addEventListener("click", async () => { await this.SignInWithKnownProvider(new OAuthProvider('yahoo.com'), butYahoo) });

        const butXTwitter = document.getElementById("butSignInWithXTwitter") as HTMLButtonElement;;
        butXTwitter?.addEventListener("click", async () => { await this.SignInWithKnownProvider(new TwitterAuthProvider(), butXTwitter) });

        // Sign-in by phone buttons, one for code request and one for code verification
        document.getElementById("SignInWithPhone")?.addEventListener("click", () => this.SignInViaPhoneStep1());
        document.getElementById("SubmitSMSCode")?.addEventListener("click", () => this.ValidatePhoneSMSCodeStep2());

        this.auth.signOut() // Make sure user is signed out in Firebase before wiring up onAuthStateChanged()
            .then(() => this.auth.onAuthStateChanged((user) => this.MyAuthStateChanged(user)));

        this.pageBusyIndicator.Decrement();
    }

    private MyAuthStateChanged(user: User | null) { // Also fires on initial page load even if no state change.
        if (user !== null) { // User is logged-in in Firebase?
            user.getIdToken(true)
                .then((FirebaseIdToken: string) => this.PostFirebaseIdTokenToBlazorForm(FirebaseIdToken)) // Decode token: https://jwt.io/
                .then(() => this.auth.signOut()) // Use authentication has completed so sign-out of Firebase
                .catch((error) => {
                    ReportErrorToServer.ReportClientErrorToServer(error.message, "MyAuthStateChanged() in SignIn.ts", error);
                    (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "An unexpected firebase sign-in error has occurred.";
                })
        }
    }

    private async SignInWithKnownProvider(provider: AuthProvider, submitButton: HTMLButtonElement): Promise<void> {
        this.pageBusyIndicator.Increment();
        submitButton.disabled = true;
        (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "";

        await signInWithPopup(this.auth, provider) // If this succeeds, onAuthStateChanged() will be called
            .catch((error) => {
                (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = error.code + "; Try again.";
                submitButton.disabled = false;
            }
            );
        this.pageBusyIndicator.Decrement();
    }

    // For testing, use fictional phone #'s (eg 949-555-2930 and code 777777) in Firebase Console
    // (under BTC Web project | Authentication | Sign-in Method | Phone | Phone #s for testing)
    private SignInViaPhoneStep1() {
        (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "";

        const phoneNumRaw = (document.getElementById("PhoneNumber") as HTMLInputElement).value;
        const phoneNumDigits = phoneNumRaw.replace(/\D/g, '') // Remove non-digits
        if (phoneNumDigits.length !== 10) {
            (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "Error: Only 10-digit USA phone #'s can be used.";
        }
        else {
            const E164Format = "+1" + phoneNumDigits; // Convert to E.164 format
            if (this.recaptchaVerifierForPhone === null)
                this.recaptchaVerifierForPhone = new RecaptchaVerifier(this.auth, 'MySMS-recaptcha-container', {});

            this.pageBusyIndicator.Increment();
            (document.getElementById("SignInWithPhone") as HTMLInputElement).style.display = "none"; // Hide phone # submit button while user processes recaptcha V2
            signInWithPhoneNumber(this.auth, E164Format, this.recaptchaVerifierForPhone)
                .then((confirmationResult) => {
                    this.confirmationResultForPhone = confirmationResult;
                    (document.getElementById("divSMSCode") as HTMLDivElement).style.display = "revert";
                }).catch((error) => { // Error; SMS not sent
                    (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = error.message;
                }).finally(() => {
                    this.pageBusyIndicator.Decrement();
                    this.recaptchaVerifierForPhone?.clear();
                    this.recaptchaVerifierForPhone = null;
                });
        }
    }

    private ValidatePhoneSMSCodeStep2() {
        this.pageBusyIndicator.Increment();

        const inputCode = document.getElementById("SMSCode") as HTMLInputElement;
        const code = inputCode.value;
        this.confirmationResultForPhone?.confirm(code)  // If this succeeds, onAuthStateChanged() will be called
            .catch((error) => {
                if (error.code === "auth/invalid-verification-code")
                    (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "Code could not be verified; try again";
                else
                    (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = error.message;
            }).finally(() => {
                this.pageBusyIndicator.Decrement();
                inputCode.value = "";
                (document.getElementById("SignInWithPhone") as HTMLInputElement).style.display = "revert";
            });
    }

    private PostFirebaseIdTokenToBlazorForm(FirebaseIdToken: string) {
        (document.getElementById("jsErrorMessage") as HTMLDivElement).innerHTML = "";

        // User does not need to remain signed-in to Firebase beyond the session
        setPersistence(this.auth, browserSessionPersistence); // https://firebase.google.com/docs/auth/web/auth-state-persistence

        // Record Firebase idToken into Blazor form and submit
        let elemFirebaseIdToken = document.getElementById("FirebaseIdToken") as HTMLInputElement;
        elemFirebaseIdToken.value = FirebaseIdToken;
        elemFirebaseIdToken.dispatchEvent(new Event('change', { 'bubbles': true })); // Tell Blazor that firebase token changed
        // Trigger Blazor to complete sign-in, save auth cookie, and page re-direct
        (document.getElementById("idSigninForm") as HTMLFormElement).requestSubmit();
    }
}
